import { Avatar, AvatarImage } from "@/components/ui/avatar";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogDescription,
  DialogTitle,
  DialogTrigger,
  DialogContent,
} from "@/components/ui/dialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableHeader,
  TableHead,
  TableBody,
  TableCell,
  TableRow,
} from "@/components/ui/table";
import { useMediaQuery } from "@/hooks/use-media-query";
import { cn } from "@/lib/utils";
import {
  Badge as BadgeIcon,
  RefreshCcw,
} from "lucide-react";
import { AddCartMethod } from "modules/cart/types/add-cart-methods";
import {
  CartItem,
  Payload,
  PriceComponent,
  ProductSearchSelectionContext,
  UnavailableAlternative,
} from "modules/cart/types/cart";
import { ProductSearchPicker } from "modules/lists/components/product-search-picker";
import { StoreData } from "modules/main/types/storeData";
import { ProductSheet } from "modules/product/components/product-sheet";
import React, { useState, useCallback, useRef } from "react";
import { forwardRef } from "react";
import { toast } from "sonner";
import Amount from "utils/amount";
import { get_total_qty_per_prod } from "utils/cartUtils";
import { isDesktopSize } from "utils/is-desktop";
import { getStoreBdData } from "utils/storeDataManager";
import { CartCreationStatus } from "../types/optimizedCart";
import {
  patchUnavailableReplacement,
  mapPickerSelectionToUnavailableAlternative as mapPickerSelToUnavailAlt,
} from "../services/unavailableReplacement";
import { useFeatureFlagEnabled } from "posthog-js/react";

interface ProductTableProps {
  className?: string;
  filterCart: CartItem[];
  cart: CartItem[];
  disableButtons?: boolean;
  loading?: boolean;
  storeList: StoreData[];
  mode: "RESULT" | "VIEW";
  noClick?: boolean;
  cartCreationStatus: CartCreationStatus[];
  setCartStatus: React.Dispatch<React.SetStateAction<CartCreationStatus[]>>;
  addDeletefromCart: (payload: Payload) => void;
  removeFromCart?: (payload: Payload) => void;
  refreshTrigger?: () => void;
  setNoReplace: (productId: string, newMethod: AddCartMethod) => void;
  notFoundsFlag?: boolean;
  /** Scenario (order) ID from OptimizedScenario.order_id, required for PATCH. */
  scenarioId?: number;
}

type SelectedUnavailableAlternatives = Record<string, UnavailableAlternative>;

interface ActiveUnavailableSearch {
  selectionKey: string;
  initialQuery: string;
  quantity: number;
  /** Store ID (PriceComponent.id) for the PATCH body. */
  storeId: number;
  /** Original catalog ID (PriceComponent.catalog_id) for the PATCH body. */
  originalCatalogId: string;
  /** Optional original SKU (PriceComponent.sku) for disambiguation. */
  originalSku?: string;
}

interface ProductTableRenderProps extends ProductTableProps {
  selectedUnavailableAlternatives: SelectedUnavailableAlternatives;
  onSelectUnavailableAlternative: (
    selectionKey: string,
    alternative: UnavailableAlternative,
    storeId: number,
    originalCatalogId: string,
    originalSku?: string,
  ) => void;
  onSearchUnavailableAlternative: (target: ActiveUnavailableSearch) => void;
}

interface UnavailableProductSummary {
  imageUrl: string;
  productName: string;
  quantity: number;
  totalPrice: number;
}

const getUnavailableSelectionKey = (
  cartItemId: string,
  priceComponent: PriceComponent,
) => `${cartItemId}:${priceComponent.sku || priceComponent.id}`;

const getUnavailableAlternativeId = (alternative: UnavailableAlternative) =>
  alternative.catalog_id || alternative.slug;

const getUnavailableProductSummary = ({
  originalSummary,
  selectedAlternative,
}: {
  originalSummary: UnavailableProductSummary;
  selectedAlternative?: UnavailableAlternative;
}): UnavailableProductSummary => {
  if (!selectedAlternative) return originalSummary;

  return {
    imageUrl: selectedAlternative.image_url,
    productName: selectedAlternative.name,
    quantity: selectedAlternative.quantity,
    totalPrice: selectedAlternative.total_price,
  };
};

export const ProductTable = forwardRef<HTMLTableElement, ProductTableProps>(
  ({ ...props }, ref) => {
    const isDesktop = useMediaQuery(isDesktopSize);
    const [selectedUnavailableAlternatives, setSelectedUnavailableAlternatives] =
      useState<SelectedUnavailableAlternatives>({});
    const [activeUnavailableSearch, setActiveUnavailableSearch] =
      useState<ActiveUnavailableSearch | null>(null);
    /** Ref to track the latest scenarioId for async undo callbacks. */
    const scenarioIdRef = useRef(props.scenarioId);
    scenarioIdRef.current = props.scenarioId;

    const handleSelectUnavailableAlternative = useCallback(
      async (
        selectionKey: string,
        alternative: UnavailableAlternative,
        storeId: number,
        originalCatalogId: string,
        originalSku?: string,
      ) => {
        const currentScenarioId = scenarioIdRef.current;
        if (currentScenarioId === undefined || currentScenarioId === 0) {
          // Fall back to local-only if no scenario context
          setSelectedUnavailableAlternatives((current) => ({
            ...current,
            [selectionKey]: alternative,
          }));
          return;
        }

        // Guard: do not send PATCH if original_catalog_id is missing, blank, or whitespace-only
        if (!originalCatalogId?.trim()) {
          toast.error("No se pudo aplicar la alternativa");
          return;
        }

        // Snapshot previous selection for rollback & undo
        let previousSelection: UnavailableAlternative | undefined;
        setSelectedUnavailableAlternatives((current) => {
          previousSelection = current[selectionKey];
          return { ...current, [selectionKey]: alternative };
        });

        // Wait for state to settle before PATCH
        // The optimistic state has been applied above via functional setter.
        try {
          await patchUnavailableReplacement(currentScenarioId, {
            store_id: storeId,
            original_catalog_id: originalCatalogId,
            ...(originalSku ? { original_sku: originalSku } : {}),
            replacement_catalog_id: alternative.catalog_id,
            ...(alternative.sku ? { replacement_sku: alternative.sku } : {}),
          });

          toast.success("Alternativa aplicada", {
            duration: 5000,
            action: {
              label: "Deshacer",
              onClick: () => {
                handleUndo(
                  selectionKey,
                  previousSelection,
                  storeId,
                  originalCatalogId,
                  originalSku,
                );
              },
            },
          });
        } catch {
          // Rollback to snapshot on error
          setSelectedUnavailableAlternatives((current) => ({
            ...current,
            [selectionKey]: previousSelection ?? null as unknown as UnavailableAlternative,
          }));
          // Clean up null entry if previous was undefined
          if (!previousSelection) {
            setSelectedUnavailableAlternatives((current) => {
              const { [selectionKey]: _, ...rest } = current;
              return rest;
            });
          }
          toast.error("No se pudo aplicar la alternativa");
        }
      },
      [],
    );

    /** Undo handler for the Deshacer toast action */
    const handleUndo = useCallback(
      async (
        selectionKey: string,
        previousSelection: UnavailableAlternative | undefined,
        storeId: number,
        originalCatalogId: string,
        originalSku?: string,
      ) => {
        const currentScenarioId = scenarioIdRef.current;

        if (previousSelection && currentScenarioId && currentScenarioId > 0) {
          // Re-PATCH with previous replacement's catalog_id
          try {
            await patchUnavailableReplacement(currentScenarioId, {
              store_id: storeId,
              original_catalog_id: originalCatalogId,
              ...(originalSku ? { original_sku: originalSku } : {}),
              replacement_catalog_id: previousSelection.catalog_id,
              ...(previousSelection.sku ? { replacement_sku: previousSelection.sku } : {}),
            });
            // Restore previous selection
            setSelectedUnavailableAlternatives((current) => ({
              ...current,
              [selectionKey]: previousSelection,
            }));
            toast.success("Alternativa restaurada", { duration: 3000 });
          } catch {
            toast.error("No se pudo deshacer el cambio");
          }
        } else {
          // Local rollback only — no previous selection
          setSelectedUnavailableAlternatives((current) => {
            const { [selectionKey]: _, ...rest } = current;
            return rest;
          });
        }
      },
      [],
    );

    const handlePickerProductSelected = useCallback(
      (payload: Payload, context?: ProductSearchSelectionContext) => {
        if (!activeUnavailableSearch) return;

        const mapped = mapPickerSelToUnavailAlt({
          payload,
          context,
          quantity: activeUnavailableSearch.quantity,
        });

        handleSelectUnavailableAlternative(
          activeUnavailableSearch.selectionKey,
          mapped,
          activeUnavailableSearch.storeId,
          activeUnavailableSearch.originalCatalogId,
          activeUnavailableSearch.originalSku,
        );
        setActiveUnavailableSearch(null);
      },
      [activeUnavailableSearch, handleSelectUnavailableAlternative],
    );

    return (
      <>
        {isDesktop ? (
          <ProductTableDesktop
            {...props}
            ref={ref}
            selectedUnavailableAlternatives={selectedUnavailableAlternatives}
            onSelectUnavailableAlternative={handleSelectUnavailableAlternative}
            onSearchUnavailableAlternative={setActiveUnavailableSearch}
          />
        ) : (
          <ProductTableMobile
            {...props}
            selectedUnavailableAlternatives={selectedUnavailableAlternatives}
            onSelectUnavailableAlternative={handleSelectUnavailableAlternative}
            onSearchUnavailableAlternative={setActiveUnavailableSearch}
          />
        )}
        <ProductSearchPicker
          open={!!activeUnavailableSearch}
          onOpenChange={(open) => {
            if (!open) setActiveUnavailableSearch(null);
          }}
          onProductSelected={handlePickerProductSelected}
          initialQuery={activeUnavailableSearch?.initialQuery ?? ""}
        />
      </>
    );
  },
);

const ProductTableDesktop = forwardRef<HTMLTableElement, ProductTableRenderProps>(
  ({ className, filterCart, ...props }, ref) => {
    const [noReplaceStates, setNoReplaceStates] = useState<{
      [key: string]: boolean;
    }>({});

    const newProductTab = (url: string) => {
      const windowName = "carriapp_product_window";
      window.open(url, windowName);
    };

    return (
      <Table className={cn("", className)} ref={ref}>
        <TableHeader>
          <TableRow>
            <TableHead className="hidden w-[100px] sm:table-cell">
              <span className="sr-only">Image</span>
            </TableHead>
            <TableHead>Producto</TableHead>
            <TableHead className="text-right">Precio Unitario</TableHead>
            {props.mode === "RESULT" && (
              <TableHead className="text-center">Cantidad</TableHead>
            )}
            {/* <TableHead className="text-center">Tienda</TableHead> */}
            {/* <TableHead className="text-right">
                        Ahorras
                    </TableHead>                     */}
            <TableHead className="text-right hidden md:table-cell">
              {props.notFoundsFlag ? "Referencia" : "Sub Total"}
            </TableHead>
            {/* <TableHead>
                    <span className="sr-only">Url</span>
                    </TableHead> */}
          </TableRow>
        </TableHeader>
        <TableBody>
          {props.loading
            ? [...Array(4)].map((_, index) => (
                <TableRow key={index}>
                  <TableCell>
                    <Skeleton className="w-full h-20" />
                  </TableCell>
                  <TableCell className="w-96">
                    <Skeleton className="w-full h-20" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="w-full h-20" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="w-full h-20" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="w-full h-20" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="w-full h-20" />
                  </TableCell>
                  {/* <TableCell><Skeleton className="w-full h-20"/></TableCell> */}
                  {/* <TableCell><Skeleton className="w-full h-20"/></TableCell> */}
                </TableRow>
              ))
            : filterCart.map((item, index) => {
                const basePc = item.price_component[0];
                const factor = basePc.factor ?? 1;
                const isPromo = item.price_component
                  .filter((pc) => !pc.no_met_conditions)
                  .some((pc) => pc.is_promo);
                const quantity = basePc.quantity;
                const price = basePc.price / (factor * quantity);
                const worst_price = item.price_component
                  .filter((pc) => !pc.no_met_conditions)
                  .reduce((acc, pc) => acc + pc.worst_price, 0);

                const cartElement = props.cart.find((ci) => ci.id === item.id);
                const imageUrl = basePc?.images?.catalog ?? basePc?.image_url;
                const replacedImageUrl =
                  basePc?.replaced_images?.thumbs ?? basePc?.replaced_image_url;

                if (props.notFoundsFlag) {
                  const selectionKey = getUnavailableSelectionKey(
                    item.id,
                    basePc,
                  );
                  const summary = getUnavailableProductSummary({
                    originalSummary: {
                      imageUrl,
                      productName: `${item.brand} - ${basePc.name}`,
                      quantity: quantity * factor,
                      totalPrice: basePc.price,
                    },
                    selectedAlternative:
                      props.selectedUnavailableAlternatives[selectionKey],
                  });

                  return (
                    <TableRow key={`${index}`} className="hover:bg-transparent">
                      <TableCell
                        colSpan={props.mode === "RESULT" ? 5 : 4}
                        className="py-2"
                      >
                        <UnavailableProductAccordion
                          alternatives={basePc.alternatives ?? []}
                          imageUrl={summary.imageUrl}
                          productName={summary.productName}
                          quantity={summary.quantity}
                          totalPrice={summary.totalPrice}
                          selectedAlternative={
                            props.selectedUnavailableAlternatives[selectionKey]
                          }
                          onSelect={(alternative) =>
                            props.onSelectUnavailableAlternative(
                              selectionKey,
                              alternative,
                              basePc.id,
                              basePc.catalog_id?.trim() || item.id || "",
                              basePc.sku,
                            )
                          }
                          onSearch={() =>
                            props.onSearchUnavailableAlternative({
                              selectionKey,
                              initialQuery: basePc.name,
                              quantity: quantity * factor,
                              storeId: basePc.id,
                              originalCatalogId: basePc.catalog_id?.trim() || item.id || "",
                              originalSku: basePc.sku,
                            })
                          }
                          value={item.id}
                        />
                      </TableCell>
                    </TableRow>
                  );
                }

                return (
                  <React.Fragment key={`${index}`}>
                    <TableRow>
                    <TableCell className="hidden sm:table-cell">
                      <div
                        className="min-w-20 cursor-pointer"
                        onClick={() => newProductTab(basePc.url)}
                      >
                        <img className="Image-80" src={imageUrl} alt={""} />
                      </div>
                    </TableCell>
                    <TableCell className="font-medium flex flex-col gap-2 items-start justify-center">
                      <Dialog>
                        <DialogTitle></DialogTitle>
                        <DialogDescription></DialogDescription>
                        <DialogTrigger>
                          <span className="font-semibold">{item.brand}</span> -{" "}
                          <span className="font-normal">{basePc.name}</span>
                        </DialogTrigger>
                        <DialogContent className="min-w-[70%]">
                          <ProductSheet productID={item.slug} />
                        </DialogContent>
                      </Dialog>
                      {(basePc.replaced_name ||
                        basePc.replaced_image_url ||
                        isPromo ||
                        !item.met_conditions) && (
                        <div className="flex flex-row gap-2 items-center">
                          {(basePc.replaced_name ||
                            basePc.replaced_image_url ||
                            replacedImageUrl) && (
                            <div className="flex flex-row gap-2 items-center">
                              <Popover>
                                <PopoverTrigger asChild>
                                  <Button
                                    variant={"secondary"}
                                    size={"xs"}
                                    className="text-foreground flex items-center"
                                  >
                                    <RefreshCcw className="w-4 h-4" />
                                    <span className="text-xxs">Reemplazo</span>
                                  </Button>
                                </PopoverTrigger>
                                <PopoverContent className="flex flex-col gap-4">
                                  <div className="flex flex-row gap-4 items-center">
                                    <div className="min-w-16">
                                      <img
                                        className="Image-60"
                                        src={replacedImageUrl}
                                        alt={""}
                                      />
                                    </div>
                                    <div className="flex flex-col gap-1">
                                      <span className="font-semibold text-xs">
                                        {basePc.replaced_name}
                                      </span>
                                      {basePc.replaced_price && (
                                        <div className="flex gap-1">
                                          <Amount
                                            className="font-semibold text-xs text-muted-foreground"
                                            amount={basePc.replaced_price}
                                            currency="CLP"
                                            locale="es-CL"
                                          />{" "}
                                          <span className="text-muted-foreground text-xxs font-semibold">
                                            c/u
                                          </span>
                                        </div>
                                      )}
                                      <div className="text-muted-foreground text-xs">
                                        <span className="font-semibold text-foreground">
                                          {quantity}
                                        </span>{" "}
                                        Unidades
                                        {basePc.replaced_price && (
                                          <Amount
                                            className="font-semibold text-foreground text-xs ml-1"
                                            amount={
                                              basePc.replaced_price * quantity
                                            }
                                            currency="CLP"
                                            locale="es-CL"
                                          />
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                  {/* <div className="flex items-center gap-2 px-2">
                                                <Switch 
                                                    id={`no-replace-${item.id}`}
                                                    checked={noReplaceStates[item.id] || false}
                                                    onCheckedChange={(checked) =>{                                                             
                                                        setNoReplaceStates(prev => ({
                                                            ...prev,
                                                            [item.id]: checked
                                                        }));
                                                        props.setNoReplace(item.id, "FIXED" as AddCartMethod)
                                                    }}                                                
                                                />
                                                <Label htmlFor="no-replace" className="text-xs">Quiero el original</Label>
                                            </div> */}
                                </PopoverContent>
                              </Popover>
                            </div>
                          )}
                          {isPromo && (
                            <div className="best-price w-full flex flex-row gap-2 items-center text-xxs">
                              <BadgeIcon className="w-4 h-4" />
                              ¡Oferta!
                            </div>
                          )}
                          {!item.met_conditions && (
                            <Badge variant="destructive">
                              No tienes el min de compra
                            </Badge>
                          )}
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      {basePc.worst_price > basePc.price && (
                        <span className="text-muted-foreground line-through">
                          <Amount
                            amount={worst_price / quantity}
                            currency="CLP"
                            locale="es-CL"
                          />
                        </span>
                      )}
                      <span>
                        <Amount
                          className="ml-2"
                          amount={price}
                          currency="CLP"
                          locale="es-CL"
                        />
                      </span>
                    </TableCell>
                    {props.mode === "RESULT" && (
                      <TableCell className="text-center">
                        <span className="font-semibold">{quantity*factor}</span>
                        {cartElement && (
                          <AdditionalStoresInfo
                            cartElement={cartElement}
                            currentStoreId={item.price_component[0]?.id}
                            storeList={props.storeList}
                          />
                        )}
                      </TableCell>
                    )}
                    <TableCell className="hidden md:table-cell text-right font-semibold">
                      <Amount
                        amount={basePc.price}
                        currency="CLP"
                        locale="es-CL"
                      />
                    </TableCell>
                    </TableRow>
                  </React.Fragment>
                );
              })}
        </TableBody>
      </Table>
    );
  },
);

export const ProductTableMobile: React.FC<ProductTableRenderProps> = ({
  filterCart,
  ...props
}) => {
  const [noReplaceStates, setNoReplaceStates] = useState<{
    [key: string]: boolean;
  }>({});

  return (
    <div className={cn("flex flex-col gap-2", props.className)}>
      {filterCart.map((ci, i) => {
        return (
          <React.Fragment key={`${i}`}>
            <ProductTableItem
              key={`${i}`}
              product={ci}
              disableButtons={props.disableButtons}
              total={get_total_qty_per_prod(ci.id, filterCart)}
              priceComponent={ci.price_component}
              brand={ci.brand}
              storeList={props.storeList}
              mode={props.mode}
              filterCart={filterCart}
              addDeletefromCart={props.addDeletefromCart}
              noReplaceStates={noReplaceStates}
              setNoReplaceStates={setNoReplaceStates}
              setNoReplace={props.setNoReplace}
              cartCreationStatus={props.cartCreationStatus}
              setCartStatus={props.setCartStatus}
              noClick={props.noClick}
              notFoundsFlag={props.notFoundsFlag}
              selectedUnavailableAlternatives={
                props.selectedUnavailableAlternatives
              }
              onSelectUnavailableAlternative={
                props.onSelectUnavailableAlternative
              }
              onSearchUnavailableAlternative={
                props.onSearchUnavailableAlternative
              }
            />
            {!(i === filterCart.length - 1) && (
              <Separator className="w-full p-0" />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

interface ProductTableItemProps extends React.HTMLAttributes<HTMLDivElement> {
  product: CartItem;
  priceComponent: PriceComponent[];
  brand: string;
  total: number;
  exceded?: boolean;
  disableButtons?: boolean;
  storeList: StoreData[];
  mode?: "RESULT" | "VIEW";
  filterCart: CartItem[];
  addDeletefromCart?: (payload: Payload) => void;
  noReplaceStates: { [key: string]: boolean };
  setNoReplaceStates: React.Dispatch<
    React.SetStateAction<{ [key: string]: boolean }>
  >;
  setNoReplace: (productId: string, newMethod: AddCartMethod) => void;
  cartCreationStatus: CartCreationStatus[];
  setCartStatus: React.Dispatch<React.SetStateAction<CartCreationStatus[]>>;
  noClick?: boolean;
  notFoundsFlag?: boolean;
  selectedUnavailableAlternatives: SelectedUnavailableAlternatives;
  onSelectUnavailableAlternative: (
    selectionKey: string,
    alternative: UnavailableAlternative,
    storeId: number,
    originalCatalogId: string,
    originalSku?: string,
  ) => void;
  onSearchUnavailableAlternative: (target: ActiveUnavailableSearch) => void;
}

const ProductTableItem = forwardRef<HTMLDivElement, ProductTableItemProps>(
  ({ product, priceComponent: p, ...props }, ref) => {
    const basePc = product.price_component[0];
    const quantity = basePc?.quantity ?? 1;
    const factor = basePc?.factor ?? 1;
    const price = basePc.price / (quantity * factor);
    const worst_price = basePc.worst_price / quantity;
    const discountPercent =
      worst_price > 0 ? Math.max(0, (worst_price - price) / worst_price) : 0;
    const isPromo = p.some((pc) => pc.is_promo);
    const no_met_conditions = basePc?.no_met_conditions;

    const imageUrl = basePc?.images?.thumbs ?? basePc?.image_url;
    const replacedImageUrl =
      basePc?.replaced_images?.thumbs ?? basePc?.replaced_image_url;

    if (props.notFoundsFlag) {
      const selectionKey = getUnavailableSelectionKey(product.id, basePc);
      const summary = getUnavailableProductSummary({
        originalSummary: {
          imageUrl,
          productName: p[0].name,
          quantity: quantity * factor,
          totalPrice: basePc.price,
        },
        selectedAlternative: props.selectedUnavailableAlternatives[selectionKey],
      });

      return (
        <UnavailableProductAccordion
          alternatives={basePc.alternatives ?? []}
          imageUrl={summary.imageUrl}
          productName={summary.productName}
          quantity={summary.quantity}
          totalPrice={summary.totalPrice}
          selectedAlternative={props.selectedUnavailableAlternatives[selectionKey]}
          onSelect={(alternative) =>
            props.onSelectUnavailableAlternative(
              selectionKey,
              alternative,
              basePc.id,
              basePc.catalog_id?.trim() || product.id || "",
              basePc.sku,
            )
          }
          onSearch={() =>
            props.onSearchUnavailableAlternative({
              selectionKey,
              initialQuery: basePc.name,
              quantity: quantity * factor,
              storeId: basePc.id,
              originalCatalogId: basePc.catalog_id?.trim() || product.id || "",
              originalSku: basePc.sku,
            })
          }
          value={product.id}
        />
      );
    }

    return (
      <div className="flex w-full flex-col gap-3">
        <div className="flex w-full flex-row gap-4 items-start">
        <div>
          <div className="min-w-20 cursor-pointer relative">
            <img className="Image-80" src={imageUrl} alt={""} />
          </div>
        </div>
        <div className="flex flex-col gap-2 w-full items-start">
          <div className="flex flex-row gap-4 items-center w-full">
            <div className="flex flex-col text-xs w-full gap-2">
              <span className="font-semibold">{p[0].name}</span>
              <div className="flex flex-row gap-2 justify-between w-full">
                <div>
                  <span className="text-xs font-semibold text-muted-foreground">
                    {props.brand}
                  </span>
                  <div className="flex gap-1">
                    <Amount
                      className="font-semibold text-muted-foreground"
                      amount={price}
                      currency="CLP"
                      locale="es-CL"
                    />{" "}
                    <span className="text-muted-foreground text-xxs font-semibold">
                      c/u
                    </span>
                    {worst_price > price && (
                      <span className="text-muted-foreground line-through ml-1">
                        <Amount
                          amount={worst_price}
                          currency="CLP"
                          locale="es-CL"
                        />
                      </span>
                    )}
                    {/* {(isPromo)&&(
                                    <Badge className="mx-2 text-xxs bg-green-600">
                                        <Amount 
                                            className="font-semibold text-white"
                                            amount={discountPercent} 
                                            formatStyle="percent"
                                            locale="es-CL" 
                                            />    
                                    </Badge>)}                                                              */}
                  </div>
                </div>
                {props.mode === "RESULT" && (
                  <span className="text-sm font-semibold">
                    {quantity*factor}
                  </span>
                )}
              </div>
            </div>
          </div>
          <Separator className="w-full p-0" />
          <div className="flex w-full flex-row justify-between items-center text-white">
            <div>
              {(basePc.replaced_name ||
                basePc.replaced_image_url ||
                replacedImageUrl) && (
                <div className="flex flex-row gap-2 items-center">
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant={"secondary"}
                        size={"xs"}
                        className="text-foreground flex items-center"
                      >
                        <RefreshCcw className="w-4 h-4" />
                        <span className="text-xxs">Reemplazo</span>
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="flex flex-col gap-4">
                      <div className="flex flex-row gap-4 items-center">
                        <div className="min-w-16">
                          <img
                            className="Image-60"
                            src={replacedImageUrl}
                            alt={""}
                          />
                        </div>
                        <div className="flex flex-col gap-1">
                          <span className="font-semibold text-xs">
                            {basePc.replaced_name}
                          </span>
                          {basePc.replaced_price && (
                            <div className="flex gap-1">
                              <Amount
                                className="font-semibold text-xs text-muted-foreground"
                                amount={basePc.replaced_price}
                                currency="CLP"
                                locale="es-CL"
                              />{" "}
                              <span className="text-muted-foreground text-xxs font-semibold">
                                c/u
                              </span>
                            </div>
                          )}
                          <div className="text-muted-foreground text-xs">
                            <span className="font-semibold text-foreground">
                              {quantity}
                            </span>{" "}
                            Unidades
                            {basePc.replaced_price && (
                              <Amount
                                className="font-semibold text-foreground text-xs ml-1"
                                amount={basePc.replaced_price * quantity}
                                currency="CLP"
                                locale="es-CL"
                              />
                            )}
                          </div>
                        </div>
                      </div>
                      {/* <div className="flex items-center gap-2 px-2">
                                    <Switch 
                                            id={`no-replace-${product.id}`}
                                            checked={props.noReplaceStates[product.id] || false}
                                            onCheckedChange={(checked) =>{ 
                                                
                                                props.setNoReplaceStates(prev => ({
                                                    ...prev,
                                                    [product.id]: checked
                                                }));
                                                props.setNoReplace(product.id, "FIXED" as AddCartMethod)
                                            }}                                                
                                    />
                                    <Label htmlFor="no-replace" className="text-xs">Quiero el original</Label>
                                </div> */}
                    </PopoverContent>
                  </Popover>
                </div>
              )}
            </div>
            {/* <div className="flex flex-col gap-2 text-xs">
                    {
                    p.map((pc,i) => {
                        return(
                        <div className="flex flex-row gap-2 items-center" key={i}>
                            <a href={pc.url} target="_blank" rel="noopener noreferrer">
                            </a>
                            <div className="flex flex-col w-full gap-2 justify-start text-white">
                                <div className={cn("flex w-full gap-2",
                                    {
                                        "opacity-40":no_met_conditions,
                                        "line-through": no_met_conditions
                                    })}>
                                    
                                </div>
                            </div>
                        </div>
                        )
                    })
                    }


                </div> */}
            <div className="text-xs">
              <span className="text-muted-foreground">
                Referencia:
                <Amount
                  className="ml-2 font-semibold text-foreground"
                  amount={basePc.price}
                  currency="CLP"
                  locale="es-CL"
                />
              </span>
            </div>
          </div>
        </div>
        </div>
        {props.notFoundsFlag && (
          <UnavailableAlternativesBlock
            alternatives={basePc.alternatives ?? []}
            className="w-full"
          />
        )}
      </div>
    );
  },
);

interface UnavailableAlternativesProps {
  alternatives: UnavailableAlternative[];
  className?: string;
  selectedAlternative?: UnavailableAlternative;
  onSelect?: (alternative: UnavailableAlternative) => void;
  onSearch?: () => void;
}

interface UnavailableProductAccordionProps {
  alternatives: UnavailableAlternative[];
  imageUrl: string;
  productName: string;
  quantity: number;
  totalPrice: number;
  value: string;
  selectedAlternative?: UnavailableAlternative;
  onSelect?: (alternative: UnavailableAlternative) => void;
  onSearch?: () => void;
}

const UnavailableProductAccordion = ({
  alternatives,
  imageUrl,
  productName,
  quantity,
  totalPrice,
  value,
  selectedAlternative,
  onSelect,
  onSearch,
}: UnavailableProductAccordionProps) => {
  const alternativesLabel =
    alternatives.length === 1
      ? "1 alternativa"
      : `${alternatives.length} alternativas`;

  return (
    <Accordion type="single" collapsible className="w-full">
      <AccordionItem value={value} className="border-b-0">
        <AccordionTrigger className="rounded-md border bg-background p-3 hover:no-underline">
          <div className="flex min-w-0 flex-1 items-center gap-3 text-left">
            <div className="min-w-16 cursor-pointer relative">
              <img className="Image-60" src={imageUrl} alt={""} />
            </div>
            <div className="flex min-w-0 flex-1 flex-col gap-1">
              <span className="line-clamp-2 text-xs font-semibold">
                {productName}
              </span>
              <div className="flex flex-wrap items-center gap-1 text-xxs text-muted-foreground">
                <span>{quantity} unidades</span>
                <span>·</span>
                <Amount
                  className="font-semibold text-foreground"
                  amount={totalPrice}
                  currency="CLP"
                  locale="es-CL"
                />
              </div>
            </div>
            <Badge variant="secondary" className="shrink-0 text-xxs">
              {alternativesLabel}
            </Badge>
          </div>
        </AccordionTrigger>
        <AccordionContent className="px-0 pb-0 pt-2">
          <UnavailableAlternativesBlock
            alternatives={alternatives}
            selectedAlternative={selectedAlternative}
            onSelect={onSelect}
            onSearch={onSearch}
          />
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
};

const UnavailableAlternativesBlock = ({
  alternatives,
  className,
  selectedAlternative,
  onSelect,
  onSearch,
}: UnavailableAlternativesProps) => (
  <div className={cn("flex flex-col gap-2", className)}>
    <p className="text-xs font-medium text-foreground">
      Elige una alternativa para reemplazar este producto o busca otra opción.
    </p>
    {selectedAlternative &&
      !alternatives.some(
        (alternative) =>
          getUnavailableAlternativeId(alternative) ===
          getUnavailableAlternativeId(selectedAlternative),
      ) && (
        <UnavailableAlternativeRow
          alternative={selectedAlternative}
          isSelected={true}
          onSelect={onSelect ?? (() => {})}
        />
      )}
    {alternatives.map((alternative, index) => (
      <UnavailableAlternativeRow
        alternative={alternative}
        isSelected={
          !!selectedAlternative &&
          getUnavailableAlternativeId(selectedAlternative) ===
            getUnavailableAlternativeId(alternative)
        }
        key={`${alternative.catalog_id || alternative.slug}-${index}`}
        onSelect={onSelect ?? (() => {})}
      />
    ))}
    <UnavailableSearchRow hasAlternatives={alternatives.length > 0} onSearch={onSearch} />
  </div>
);

const UnavailableAlternativeRow = ({
  alternative,
  isSelected,
  onSelect,
}: {
  alternative: UnavailableAlternative;
  isSelected: boolean;
  onSelect: (alternative: UnavailableAlternative) => void;
}) => (
  <div
    className={cn(
      "flex items-center justify-between gap-3 rounded-md border bg-background p-2",
      isSelected && "border-primary bg-accent/50",
    )}
  >
    <div className="flex min-w-0 items-center gap-3">
      <img
        className="Image-60 rounded-md object-cover"
        src={alternative.image_url}
        alt={alternative.name}
      />
      <div className="flex min-w-0 flex-col gap-1">
        <span className="line-clamp-2 text-xs font-semibold">
          {alternative.name}
        </span>
        <span className="text-xxs text-muted-foreground">
          {alternative.quantity} unidades
        </span>
        <div className="flex flex-wrap items-center gap-1 text-xxs text-muted-foreground">
          <Amount amount={alternative.price} currency="CLP" locale="es-CL" />
          <span>c/u</span>
          <span>·</span>
          <Amount
            className="font-semibold text-foreground"
            amount={alternative.total_price}
            currency="CLP"
            locale="es-CL"
          />
        </div>
      </div>
    </div>
    {isSelected ? (
      <Badge variant="secondary" className="shrink-0 text-xxs">
        Seleccionado
      </Badge>
    ) : (
      <Button
        type="button"
        variant="secondary"
        size="sm"
        onClick={() => onSelect(alternative)}
      >
        Usar
      </Button>
    )}
  </div>
);

const UnavailableSearchRow = ({
  hasAlternatives,
  onSearch,
}: {
  hasAlternatives: boolean;
  onSearch?: () => void;
}) => {
  const searchPickerEnabled = useFeatureFlagEnabled("replace-search", false);

  if (!searchPickerEnabled) return null;

  return (
    <div className="flex items-center justify-between rounded-md border border-dashed bg-background p-2 text-xs text-muted-foreground">
      {hasAlternatives ? (
        <>
          <span>¿Quieres revisar otra opción?</span>
          <Button type="button" variant="outline" size="sm" onClick={onSearch}>
            Buscar más
          </Button>
        </>
      ) : (
        <Button type="button" variant="outline" size="sm" className="w-full" onClick={onSearch}>
          No encontramos alternativas sugeridas. Buscar más
        </Button>
      )}
    </div>
  );
};

const getOtherStoreComponents = (
  cartElement: CartItem,
  currentStoreId: number,
) => {
  if (!cartElement?.price_component) return [];
  return cartElement.price_component.filter((pc) => pc.id !== currentStoreId);
};

// Componente separado para las tiendas adicionales
const AdditionalStoresInfo = ({
  cartElement,
  currentStoreId,
  storeList,
}: {
  cartElement: CartItem;
  currentStoreId: number;
  storeList: StoreData[];
}) => {
  const otherStores = getOtherStoreComponents(cartElement, currentStoreId);

  if (otherStores.length === 0) return null;

  const totalQuantity = otherStores.reduce((acc, pc) => acc + pc.quantity, 0);

  return (
    <Badge
      variant="outline"
      className="flex flex-row w-fit mx-auto px-4 items-center justify-center gap-2 text-xs my-2 font-semibold"
    >
      <span>{totalQuantity} más en</span>
      {otherStores.map((pc, index) => (
        <Avatar key={index} className="w-4 h-4">
          <AvatarImage src={getStoreBdData(pc.id, storeList)?.logo_url} />
        </Avatar>
      ))}
    </Badge>
  );
};
